// BookingEngineService — the public side (A4 read, A5 write; RESERVATION-API-BRIEF §7–§8).
//
// Anonymous by design, so the protection is here and in the RPC, not in a
// capability: the input bounds (§7.5, Q4) are refused BEFORE any database
// round trip, so a crawler cannot make one request cost a year of rows. One
// RPC per request; the price is computed by pms-core's pure functions and
// signed here.

import {
  err,
  offerAvailability,
  ok,
  quoteStay,
  renderTermsText,
  type BookingEngineService,
  type CategoryAvailability,
  type Clock,
  type DirectBookingCommand,
  type DirectBookingRepository,
  type DirectBookingRequest,
  type DirectBookingResult,
  type IsoDate,
  type PublicOfferRepository,
  type PublicTerms,
  type QuoteQuery,
  type QuoteResult,
  type Result,
  type StayQuery,
} from '@lemontaigu/pms-core';
import { signQuote, verifyQuote } from './quoteToken.js';

/** Q4: sold up to 18 months ahead, stays of 1–30 nights. */
export const SALE_HORIZON_MONTHS = 18;
export const MAX_NIGHTS = 30;
export const MAX_ADULTS = 6;
const MAX_PERSONS = 8;

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function addMonths(date: IsoDate, months: number): IsoDate {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

function nightsBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The bounds, as one French sentence or null. Exported for its tests. */
export function stayQueryProblem(q: StayQuery, today: IsoDate): string | null {
  if (!ISO.test(q.from) || !ISO.test(q.to) || Number.isNaN(Date.parse(q.from)) || Number.isNaN(Date.parse(q.to))) {
    return 'Dates invalides (AAAA-MM-JJ).';
  }
  if (q.from < today) return "L'arrivée ne peut pas être dans le passé.";
  if (q.to > addMonths(today, SALE_HORIZON_MONTHS)) return `Réservable jusqu'à ${SALE_HORIZON_MONTHS} mois à l'avance.`;
  const n = nightsBetween(q.from, q.to);
  if (n < 1 || n > MAX_NIGHTS) return `Séjour de 1 à ${MAX_NIGHTS} nuits.`;
  for (const [k, v] of [
    ['adults', q.adults],
    ['children', q.children],
    ['infants', q.infants],
  ] as const) {
    if (!Number.isInteger(v) || v < 0) return `Nombre invalide (${k}).`;
  }
  if (q.adults < 1 || q.adults > MAX_ADULTS) return `De 1 à ${MAX_ADULTS} adultes.`;
  if (q.adults + q.children + q.infants > MAX_PERSONS) return `${MAX_PERSONS} personnes au plus.`;
  if (!/^[A-Z0-9_-]{1,20}$/.test(q.propertyCode)) return 'Établissement invalide.';
  return null;
}

export class BookingEngineServiceImpl implements BookingEngineService {
  constructor(
    private readonly offers: PublicOfferRepository,
    private readonly clock: Clock,
    private readonly quoteSecret: string,
    private readonly bookings: DirectBookingRepository | null = null,
  ) {}

  async availability(query: StayQuery): Promise<Result<CategoryAvailability[]>> {
    const problem = stayQueryProblem(query, this.clock.todayParis());
    if (problem) return err('validation', problem, { retryable: false });
    const facts = await this.offers.offer(query.propertyCode, query.from, query.to);
    if (!facts.ok) return facts;
    return ok(offerAvailability(facts.value, query));
  }

  async quote(query: QuoteQuery): Promise<Result<QuoteResult>> {
    const problem = stayQueryProblem(query, this.clock.todayParis());
    if (problem) return err('validation', problem, { retryable: false });
    const facts = await this.offers.offer(query.propertyCode, query.from, query.to);
    if (!facts.ok) return facts;

    const party = { adults: query.adults, children: query.children, infants: query.infants };
    const outcome = quoteStay(facts.value, query.category, query.plan, party);
    if (!outcome.ok) {
      return ok(
        outcome.minStay === undefined
          ? { sellable: false, reason: outcome.reason }
          : { sellable: false, reason: outcome.reason, minStay: outcome.minStay },
      );
    }
    const now = new Date(this.clock.now());
    const { quoteId, expiresAt } = await signQuote(outcome.quote, this.quoteSecret, now);
    // The plan's terms for THIS stay (PB7b): dates and amounts the guest accepts
    // at checkout, rendered by the one renderer rather than typed by the site.
    // Unsigned on purpose: the booking copies the plan's rules at sale, so the
    // text is a display of what will be recorded, not part of the price.
    const rules = facts.value.plans.find((p) => p.code === query.plan)?.terms ?? null;
    const terms =
      rules === null
        ? null
        : renderTermsText(rules, { arrival: query.from, totalCents: outcome.quote.totalCents, bookedOn: this.clock.todayParis() });
    return ok({ sellable: true, signed: { quote: outcome.quote, quoteId, expiresAt }, terms });
  }

  async terms(propertyCode: string): Promise<Result<PublicTerms[]>> {
    if (!/^[A-Z0-9_-]{1,20}$/.test(propertyCode)) return err('validation', 'Établissement invalide.', { retryable: false });
    const r = await this.offers.terms(propertyCode);
    if (!r.ok) return r;
    return ok(r.value.map((p) => ({ code: p.code, plans: p.plans, text: renderTermsText(p.rules) })));
  }

  async reserve(request: DirectBookingRequest, bodyHash: string): Promise<Result<DirectBookingResult>> {
    if (this.bookings === null) return err('integration', 'réservation en ligne non configurée');
    const problem = directBookingProblem(request);
    if (problem) return err('validation', problem, { retryable: false });
    if (!/^[0-9a-f]{64}$/.test(bodyHash)) return err('validation', 'empreinte de requête invalide', { retryable: false });

    // Every room's quote must be ours, live, and for the same stay (Q4). An
    // expired or tampered one is « price_changed »: the site re-quotes.
    const now = new Date(this.clock.now());
    const rooms: DirectBookingCommand['rooms'] = [];
    let stay: { propertyCode: string; from: string; to: string } | null = null;
    for (const room of request.rooms) {
      const v = await verifyQuote(room.quoteId, this.quoteSecret, now);
      if (!v.ok) return ok({ booked: false, reason: 'price_changed' });
      const q = v.payload.quote;
      if (stay === null) stay = { propertyCode: q.propertyCode, from: q.from, to: q.to };
      else if (q.propertyCode !== stay.propertyCode || q.from !== stay.from || q.to !== stay.to) {
        return err('validation', 'Tous les logements doivent être du même établissement et aux mêmes dates.', {
          retryable: false,
        });
      }
      rooms.push({
        categoryCode: q.category,
        planCode: q.plan,
        checkin: q.from,
        checkout: q.to,
        adults: q.party.adults,
        children: q.party.children,
        infants: q.party.infants,
        occupantName: room.occupantName?.trim() || null,
        extras: room.extras,
        nights: q.nights,
      });
    }
    if (stay === null) return err('validation', 'Au moins un logement.', { retryable: false });

    const written = await this.bookings.book({
      envelope: { source: 'direct', externalRef: request.idempotencyKey, revision: bodyHash, propertyCode: stay.propertyCode },
      guest: {
        lastName: request.guest.lastName.trim(),
        firstName: request.guest.firstName.trim(),
        email: request.guest.email.trim(),
        phone: request.guest.phone.trim(),
        lang: request.guest.lang,
      },
      booking: {
        message: request.message?.trim() || null,
        arrivalFrom: request.arrivalFrom ?? null,
        arrivalTo: request.arrivalTo ?? null,
      },
      rooms,
    });
    if (!written.ok) {
      const reason =
        written.error.kind === 'unavailable'
          ? 'full'
          : written.error.kind === 'conflict'
            ? 'idempotency_conflict'
            : written.error.kind === 'forbidden'
              ? 'not_open'
              : null;
      return reason === null ? written : ok({ booked: false, reason });
    }
    return ok({
      booked: true,
      reservationRef: written.value.ref,
      totalCents: written.value.totalCents,
      depositCents: written.value.depositCents,
      replay: written.value.replay,
    });
  }
}

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The POST body's shape, as one French sentence or null. Exported for tests. */
export function directBookingProblem(r: DirectBookingRequest): string | null {
  if (!/^[A-Za-z0-9-]{8,64}$/.test(r.idempotencyKey ?? '')) return "Clé d'idempotence invalide.";
  if (!Array.isArray(r.rooms) || r.rooms.length < 1 || r.rooms.length > 4) return 'De 1 à 4 logements.';
  for (const room of r.rooms) {
    if (typeof room.quoteId !== 'string' || room.quoteId.length > 4096) return 'Devis invalide.';
    if (!Array.isArray(room.extras) || room.extras.length > 20) return 'Extras invalides.';
    for (const x of room.extras) {
      if (typeof x.code !== 'string' || !/^[A-Za-z0-9_-]{1,40}$/.test(x.code)) return 'Extra invalide.';
      if (!Number.isInteger(x.quantity) || x.quantity < 1 || x.quantity > 20) return "Quantité d'extra invalide.";
    }
    if (room.occupantName != null && (typeof room.occupantName !== 'string' || room.occupantName.length > 80))
      return "Nom d'occupant invalide.";
  }
  const g = r.guest;
  if (!g || typeof g !== 'object') return 'Coordonnées requises.';
  for (const [v, label] of [
    [g.lastName, 'Nom'],
    [g.firstName, 'Prénom'],
  ] as const) {
    if (typeof v !== 'string' || v.trim() === '' || v.length > 80) return `${label} requis.`;
  }
  if (typeof g.email !== 'string' || g.email.length > 254 || !EMAIL.test(g.email.trim())) return 'E-mail invalide.';
  if (typeof g.phone !== 'string' || !/^[+0-9 ().-]{6,30}$/.test(g.phone.trim())) return 'Téléphone invalide.';
  if (!['fr', 'en', 'es', 'de'].includes(g.lang)) return 'Langue invalide.';
  if (r.arrivalFrom != null && !HHMM.test(r.arrivalFrom)) return "Heure d'arrivée invalide.";
  if (r.arrivalTo != null && !HHMM.test(r.arrivalTo)) return "Heure d'arrivée invalide.";
  if (r.message != null && (typeof r.message !== 'string' || r.message.length > 1000)) return 'Message trop long.';
  if (r.acceptTerms !== true) return 'Les conditions doivent être acceptées.';
  return null;
}
